import { useEffect, useState } from "react";
import { PRODUCT_NAME } from "@pv/branding";
import { api, clearAuth, getOrgId, getToken, setAuth, type AuthUser } from "./api.js";
import Dashboard from "./Dashboard.js";
import ProjectWorkspace from "./Project.js";

type Route = { view: "auth" } | { view: "dash" } | { view: "project"; id: string; name: string };

export default function App(): JSX.Element {
  const [route, setRoute] = useState<Route>(getToken() && getOrgId() ? { view: "dash" } : { view: "auth" });
  const [user, setUser] = useState<AuthUser | null>(null);

  function logout(): void {
    void api.logout().catch(() => undefined);
    clearAuth();
    setUser(null);
    setRoute({ view: "auth" });
  }

  return (
    <>
      <header className="topbar">
        <div className="logo" onClick={() => route.view !== "auth" && setRoute({ view: "dash" })}>
          <div className="mark">☀</div>
          <b>{PRODUCT_NAME}</b>
        </div>
        <div className="crumb">
          {route.view === "project" ? (
            <>
              <span style={{ cursor: "pointer", marginRight: 10, fontSize: 16 }} onClick={() => setRoute({ view: "dash" })}>←</span>
              <b>{route.name}</b>
            </>
          ) : route.view === "dash" ? (
            "Projects"
          ) : (
            "Sign in"
          )}
        </div>
        <div className="ribbon">
          {user && <span className="rb">👤 {user.name}</span>}
          {route.view !== "auth" && (
            <button className="rb" onClick={logout}>Sign out</button>
          )}
          <span className="rb">🌐 EN</span>
        </div>
      </header>

      {route.view === "auth" && (
        <AuthScreen
          onDone={(u) => {
            setUser(u);
            setRoute({ view: "dash" });
          }}
        />
      )}
      {route.view === "dash" && (
        <Dashboard onOpenProject={(id, name) => setRoute({ view: "project", id, name })} />
      )}
      {route.view === "project" && (
        <ProjectWorkspace projectId={route.id} onBack={() => setRoute({ view: "dash" })} />
      )}
    </>
  );
}

function AuthScreen({ onDone }: { onDone: (u: AuthUser) => void }): JSX.Element {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setError("");
    setBusy(true);
    try {
      if (mode === "register") {
        const res = await api.register({ email, name, password, organizationName: orgName });
        setAuth(res.token, res.organization.id);
        onDone(res.user);
      } else {
        const res = await api.login({ email, password });
        const org = res.organizations[0];
        if (!org) throw new Error("no organization for this account");
        setAuth(res.token, org.id);
        onDone(res.user);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="card auth-card">
        <h1 style={{ marginBottom: 4 }}>{mode === "login" ? "Sign in" : "Create your company account"}</h1>
        <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
          Professional PV planning, simulation, financial modeling, and documentation workflow.
        </div>
        {/* E36: approved pilot positioning statement, verbatim */}
        <div className="muted" style={{ marginBottom: 16, fontSize: 12 }}>
          Controlled design-partner pilot. Outputs are planning and documentation support. Final
          technical, legal, grid, and commercial review remains with the responsible professional or company.
        </div>
        {mode === "register" && (
          <>
            <div className="field"><label>Your name</label><input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="field"><label>Company name</label><input value={orgName} onChange={(e) => setOrgName(e.target.value)} /></div>
          </>
        )}
        <div className="field"><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="field"><label>Password {mode === "register" && <span className="muted">(min. 10 characters)</span>}</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void submit()} /></div>
        {error && <div className="msg err">{error}</div>}
        <button className="btn btn-primary" style={{ width: "100%", marginTop: 6 }} disabled={busy} onClick={() => void submit()}>
          {busy ? <span className="spinner" /> : mode === "login" ? "Sign in" : "Create account"}
        </button>
        <div style={{ marginTop: 12, fontSize: 12.5, textAlign: "center" }}>
          {mode === "login" ? (
            <>No account? <a href="#" onClick={(e) => { e.preventDefault(); setMode("register"); }}>Register your company</a></>
          ) : (
            <>Already registered? <a href="#" onClick={(e) => { e.preventDefault(); setMode("login"); }}>Sign in</a></>
          )}
        </div>
      </div>
    </div>
  );
}
